import type { ReactNode } from "react";

import { cn } from "@/lib/cn";

/**
 * What a day can be.
 *
 * Four kinds, and every date surface in the app draws them the same way. They
 * did not use to: a leave day was brand violet in the week strip on an
 * employee's home screen and ochre in the month grid three clicks away, and a
 * public holiday was iris in one place and undifferentiated grey text in the
 * other — so the same day changed colour depending on which screen you had
 * reached it from.
 */
export type DayKind = "shift" | "leave" | "holiday" | "rest";

interface Look {
  /** The block in a month cell: a 50 fill, a 100 ring, 700 text. */
  chip: string;
  /** The marker on a phone, where a cell is fifty pixels wide. */
  dot: string;
  /** What it is called, for the key and for a screen reader. */
  name: string;
}

/**
 * The palette.
 *
 * Deliberately the same fill/ring/text shape as `StatusChip`, so a day and a
 * status read as the same kind of object — because they are. Shift is the
 * brand, since being at work is the ordinary case and the accent is what the
 * app is about. Leave is ochre: somebody is away and it affects cover.
 * Holiday is iris, because it applies to everybody and is not a decision
 * anybody made. A rest day is grey, and grey is the point — it is the absence
 * of anything to say.
 */
export const DAY_LOOK: Record<DayKind, Look> = {
  shift: {
    chip: "bg-brand-50 text-brand-800 ring-1 ring-inset ring-brand-100",
    dot: "bg-brand-500",
    name: "On a shift",
  },
  leave: {
    chip: "bg-ochre-50 text-ochre-700 ring-1 ring-inset ring-ochre-100",
    dot: "bg-ochre-500",
    name: "On leave",
  },
  holiday: {
    chip: "bg-iris-50 text-iris-700 ring-1 ring-inset ring-iris-100",
    dot: "bg-iris-500",
    name: "Public holiday",
  },
  rest: {
    chip: "bg-ink-100 text-fg-muted ring-1 ring-inset ring-ink-200",
    dot: "bg-ink-400",
    name: "Day off",
  },
};

/** A block in a day cell: what it is, and under it whatever else is known. */
export function DayChip({
  kind,
  title,
  detail,
  className,
}: {
  kind: DayKind;
  title: string;
  /** A second and third line, e.g. a start time and a branch. */
  detail?: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "block rounded-xs px-1.5 py-1 text-left",
        DAY_LOOK[kind].chip,
        className,
      )}
    >
      <span className="block truncate text-xs font-semibold leading-tight">{title}</span>
      {detail}
    </span>
  );
}

/** The marker a phone gets, where there is no room for a chip. */
export function DayDot({ kind, className }: { kind: DayKind; className?: string }) {
  return (
    <span
      className={cn("block size-2 shrink-0 rounded-full", DAY_LOOK[kind].dot, className)}
      aria-hidden="true"
    />
  );
}

/** The key under a month grid. Only lists the kinds that are actually on it. */
export function DayKey({ kinds, className }: { kinds: DayKind[]; className?: string }) {
  return (
    <p className={cn("flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-fg-subtle", className)}>
      {kinds.map((kind) => (
        <span key={kind} className="flex items-center gap-1.5">
          <DayDot kind={kind} />
          {DAY_LOOK[kind].name}
        </span>
      ))}
    </p>
  );
}
